import * as React from "react";
import { Box, Paper, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

const PedigreeVisualization = ({ pedigreeData, sampleData, sampleId }) => {
  if (!pedigreeData || Object.keys(pedigreeData).length === 0) {
    return (
      <Paper elevation={6} sx={{ padding: 2, backgroundColor: "#f5f5f5" }}>
        <Typography variant="caption" color="text.secondary">
          No segregation data available
        </Typography>
      </Paper>
    );
  }

  const members = Object.entries(pedigreeData).filter(
    ([key]) => key !== "family_id" && key !== "affected_members"
  );

  // Organize members by generation and role
  const parents = members.filter(
    ([key]) =>
      key.toLowerCase().includes("father") ||
      key.toLowerCase().includes("mother")
  );

  const proband = members.filter(([key]) =>
    key.toLowerCase().includes("proband")
  );

  // Get all siblings - handle both single sibling and array of siblings
  const siblings = members.filter(([key, value]) => {
    const keyLower = key.toLowerCase();
    // Check if it's a sibling entry
    if (keyLower === "siblings" || keyLower === "sibling") {
      return false; // Skip the parent "siblings" key, we'll handle its children
    }
    // Include if it's a direct sibling reference
    return (
      (keyLower.includes("sibling") ||
        keyLower.includes("sister") ||
        keyLower.includes("brother")) &&
      !keyLower.includes("proband")
    );
  });

  // Check if there's a "siblings" array in the data
  const allSiblings = [...siblings];
  const siblingsArray = pedigreeData.siblings || pedigreeData.Siblings;
  if (siblingsArray && Array.isArray(siblingsArray)) {
    // Add each sibling from the array
    siblingsArray.forEach((siblingData, index) => {
      allSiblings.push([`sibling_${index + 1}`, siblingData]);
    });
  } else if (siblingsArray && typeof siblingsArray === "object") {
    // If siblings is an object with numbered keys
    Object.entries(siblingsArray).forEach(([key, value]) => {
      allSiblings.push([`sibling_${key}`, value]);
    });
  }

  const formatGenotype = (genotype) => {
    if (!genotype || genotype === "N/A") return "N/A";
    // Shorten long indels
    if (genotype.length > 7) {
      return genotype.substring(0, 3) + "...";
    }
    return genotype;
  };

  const getZygosityFromAF = (af) => {
    if (!af || af === "N/A") return null;
    const afValue = parseFloat(af);
    if (isNaN(afValue)) return null;
    if (afValue === 0) return "WT";
    if (afValue >= 0.8) return "HOM";
    if (afValue > 0) return "HET";
    return null;
  };

  const getGenotypeColor = (gt, af) => {
    // First try to determine from allele frequency
    if (af && af !== "N/A") {
      const afValue = parseFloat(af);
      if (!isNaN(afValue)) {
        if (afValue === 0) return "#bdbdbd"; // Wild-type
        if (afValue >= 0.8) return "#ff9800"; // Homozygous
        if (afValue > 0) return "#2196f3"; // Heterozygous
      }
    }

    // Fallback to genotype-based color
    if (!gt || gt === "N/A" || gt === "0/0" || gt === "./." || gt === "0|0")
      return "#bdbdbd";
    if (gt.includes("1/1") || gt.includes("1|1")) return "#ef5350";
    if (
      gt.includes("0/1") ||
      gt.includes("1/0") ||
      gt.includes("0|1") ||
      gt.includes("1|0")
    )
      return "#ff9800";
    return "#42a5f5";
  };

  const renderMember = (
    member,
    memberData,
    isProband = false,
    isOriginalSample = false
  ) => {
    const genotype = memberData.genotype || "N/A";
    const readDepth = memberData.read_depth || "N/A";
    const alleleFrequency = memberData.allele_frequency || "N/A";
    const isAffected =
      memberData.affected === true || memberData.affected === "true";
    const isMale =
      member.toLowerCase().includes("father") ||
      member.toLowerCase().includes("son") ||
      member.toLowerCase().includes("brother");

    const zygosity = getZygosityFromAF(alleleFrequency);
    const color = getGenotypeColor(genotype, alleleFrequency);

    return (
      <Box
        key={member}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          margin: "6px",
          position: "relative",
        }}
      >
        {/* Proband arrow indicator */}
        {isProband && (
          <Box
            sx={{
              position: "absolute",
              top: -8,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "1rem",
              color: "#666",
            }}
          >
            ↓
          </Box>
        )}

        {/* Original Sample Badge */}
        {isOriginalSample && (
          <Box
            sx={{
              position: "absolute",
              top: -10,
              right: -10,
              backgroundColor: "#4caf50",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              zIndex: 1,
            }}
          >
            <PersonIcon sx={{ fontSize: 14, color: "white" }} />
          </Box>
        )}

        {/* Member symbol */}
        <Box
          sx={{
            width: 38,
            height: 38,
            border: isOriginalSample
              ? `3px solid #4caf50`
              : `2.5px solid ${color}`,
            borderRadius: isMale ? "3px" : "50%",
            backgroundColor: isAffected ? "#424242" : "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              isProband || isOriginalSample
                ? "0 2px 8px rgba(0,0,0,0.3)"
                : "0 1px 3px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: isAffected ? "white" : "#333",
            }}
          >
            {formatGenotype(genotype)}
          </Typography>
        </Box>

        {/* Zygosity badge */}
        {zygosity && (
          <Box
            sx={{
              backgroundColor: isOriginalSample ? "#4caf50" : color,
              color: "white",
              fontSize: "0.55rem",
              fontWeight: 700,
              padding: "1px 4px",
              borderRadius: "3px",
              marginTop: "2px",
            }}
          >
            {zygosity}
          </Box>
        )}

        {/* Member label */}
        <Typography
          sx={{
            fontSize: "0.6rem",
            fontWeight: isProband || isOriginalSample ? 700 : 600,
            marginTop: "2px",
            textAlign: "center",
            maxWidth: "60px",
            color: isOriginalSample ? "#4caf50" : isProband ? "#333" : "#555",
          }}
        >
          {isOriginalSample ? "Sample" : member.replace(/_/g, " ")}
        </Typography>

        {/* AF and Depth */}
        <Typography
          sx={{
            fontSize: "0.55rem",
            color: "#666",
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          AF:{" "}
          {alleleFrequency !== "N/A"
            ? parseFloat(alleleFrequency).toFixed(2)
            : "N/A"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.55rem",
            color: "#888",
            textAlign: "center",
          }}
        >
          DP: {readDepth}
        </Typography>
      </Box>
    );
  };

  // Build children list: original sample (if provided) + proband + siblings
  const allChildren = [];

  // Add original sample if provided
  if (sampleData) {
    allChildren.push(["Sample", sampleData]);
  }

  // Add proband
  allChildren.push(...proband);

  // Add siblings
  allChildren.push(...allSiblings);

  return (
    <Paper
      elevation={6}
      sx={{
        padding: 2,
        backgroundColor: "#fafafa",
        minWidth: 240,
        maxWidth: 500,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          marginBottom: 1.5,
          display: "block",
          textAlign: "center",
          color: "#555",
          fontSize: "0.7rem",
        }}
      >
        Segregation Analysis
        {sampleId && (
          <Typography
            component="span"
            sx={{
              display: "block",
              fontSize: "0.65rem",
              color: "#4caf50",
              fontWeight: 600,
              mt: 0.5,
            }}
          >
            Sample ID: {sampleId}
          </Typography>
        )}
      </Typography>

      {/* Parents */}
      {parents.length > 0 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            {parents.map(([member, memberData]) =>
              renderMember(member, memberData)
            )}
          </Box>

          {/* Connecting line */}
          {allChildren.length > 0 && (
            <Box
              sx={{
                width: "1.5px",
                height: "15px",
                backgroundColor: "#aaa",
                margin: "0 auto",
              }}
            />
          )}
        </Box>
      )}

      {/* Children (Original Sample, Proband and Siblings) */}
      {allChildren.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {allChildren.map(([member, memberData], index) => {
            const isOriginalSample = sampleData && index === 0;
            const isProbandMember = member.toLowerCase().includes("proband");
            return renderMember(
              member,
              memberData,
              isProbandMember,
              isOriginalSample
            );
          })}
        </Box>
      )}

      {/* Compact Legend */}
      <Box
        sx={{
          marginTop: 1.5,
          paddingTop: 1.5,
          borderTop: "1px solid #ddd",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 0.5,
            fontSize: "0.6rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                border: "2px solid #999",
                borderRadius: "2px",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontSize="0.6rem">
              Male
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                border: "2px solid #999",
                borderRadius: "50%",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontSize="0.6rem">
              Female
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                backgroundColor: "#424242",
                border: "2px solid #999",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontSize="0.6rem">
              Affected
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="caption"
              fontSize="0.6rem"
              sx={{ display: "flex", alignItems: "center" }}
            >
              ↓ Proband
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                border: "2.5px solid #ff9800",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontSize="0.6rem">
              HOM (AF≥0.8)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                border: "2.5px solid #2196f3",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontSize="0.6rem">
              HET (AF&gt;0)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                border: "2.5px solid #bdbdbd",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontSize="0.6rem">
              WT (AF=0)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                border: "3px solid #4caf50",
                borderRadius: "50%",
                position: "relative",
                flexShrink: 0,
              }}
            >
              <PersonIcon
                sx={{
                  fontSize: 10,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#4caf50",
                }}
              />
            </Box>
            <Typography variant="caption" fontSize="0.6rem">
              Original Sample
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PedigreeVisualization;
